import React, { useState, useEffect, useCallback} from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Package, 
  Plus, 
  Search,
  Eye,
  CheckCircle2,
  XCircle,
  Loader2,
  DollarSign,
  Calendar
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GRList = () => {
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const [loading, setLoading] = useState(true);
  const [grs, setGrs] = useState([]);
  const [filteredGrs, setFilteredGrs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  useEffect(() => {
    loadGRs();
  }, []);


  const loadGRs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/goods-receipts`);
      setGrs(response.data);
    } catch (error) {
      console.error('Error loading GRs:', error);
      toast.error('Failed to load goods receipts');
    } finally {
      setLoading(false);
    }
  };

  const filterGRs = useCallback(() => {
    let filtered = [...grs];

    // Status filter
    if (statusFilter !== 'ALL') {
      filtered = filtered.filter(gr => gr.status === statusFilter);
    }

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(gr => 
        (gr.material_document && gr.material_document.toLowerCase().includes(term)) ||
        (gr.po_number && gr.po_number.toLowerCase().includes(term)) ||
        (gr.created_by && gr.created_by.toLowerCase().includes(term)) ||
        (gr.vendor_id && gr.vendor_id.toLowerCase().includes(term)) ||
        (gr.vendor_name && gr.vendor_name.toLowerCase().includes(term))
      );
    }

    setFilteredGrs(filtered);
  }, [grs, searchTerm, statusFilter]);

  useEffect(() => {
    filterGRs();
  }, [filterGRs]);

  const getStatusColor = (status) => {
    const colors = {
      'POSTED': 'bg-green-100 text-green-800',
      'REVERSED': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const getStats = () => {
    return {
      total: grs.length,
      posted: grs.filter(gr => gr.status === 'POSTED').length,
      reversed: grs.filter(gr => gr.status === 'REVERSED').length
    };
  };

  const stats = getStats();

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-orange-600 mx-auto mb-4" />
            <p className="text-gray-600">Loading goods receipts...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Package className="w-7 h-7 text-orange-600" />
              Goods Receipts
            </h1>
            <p className="text-sm text-gray-600 mt-1">{filteredGrs.length} of {grs.length} goods receipts</p>
          </div>
          {canWrite('goods_receipt') && (
          <Button 
            onClick={() => navigate('/p2p/gr-create')}
            className="bg-orange-600 hover:bg-orange-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Post GR
          </Button>
          )}
        </div>

        {/* Search and Filter */}
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="Search by GR number, PO number, or creator..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            >
              <option value="ALL">All Status</option>
              <option value="POSTED">Posted</option>
              <option value="REVERSED">Reversed</option>
            </select>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total GRs</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <Package className="w-8 h-8 text-orange-500" />
            </div>
          </div>
          <div className="bg-green-50 rounded-lg border border-green-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600">Posted</p>
                <p className="text-2xl font-bold text-green-700">{stats.posted}</p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-green-500" />
            </div>
          </div>
          <div className="bg-red-50 rounded-lg border border-red-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-600">Reversed</p>
                <p className="text-2xl font-bold text-red-700">{stats.reversed}</p>
              </div>
              <XCircle className="w-8 h-8 text-red-500" />
            </div>
          </div>
        </div>

        {/* GR List */}
        <div className="bg-white rounded-lg shadow-sm border">
          {filteredGrs.length === 0 ? (
            <div className="text-center py-16">
              <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No goods receipts yet</h3>
              <p className="text-gray-600 mb-4">Post your first goods receipt to get started</p>
              {canWrite('goods_receipt') && (
              <Button onClick={() => navigate('/p2p/gr-create')}>
                <Plus className="w-4 h-4 mr-2" />
                Post First GR
              </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">GR Document</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">PO Number</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendor</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Qty / Items</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Posting Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredGrs.map((gr) => (
                    <tr key={`${gr.material_document}-${gr.material_document_year}`} className="hover:bg-gray-50">
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="text-sm font-medium text-orange-600">{gr.material_document}</span>
                        <span className="text-xs text-gray-500 block">/ {gr.material_document_year}</span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="text-sm font-medium text-gray-900">{gr.po_number}</span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900">{gr.vendor_id}</span>
                        {gr.vendor_name && <span className="text-xs text-gray-500 block truncate max-w-[120px]" title={gr.vendor_name}>{gr.vendor_name}</span>}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(gr.status)}`}>
                          {gr.status === 'POSTED' ? <CheckCircle2 className="w-3 h-3 mr-1" /> : <XCircle className="w-3 h-3 mr-1" />}
                          {gr.status}
                        </span>
                        {gr.movement_type === '102' && <span className="text-xs text-yellow-600 block">Reversal</span>}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className={`font-medium ${(gr.total_quantity || 0) < 0 ? 'text-red-600' : ''}`}>
                          {(gr.total_quantity || 0).toFixed(2)}
                        </span>
                        <span className="text-xs text-gray-500 block">{gr.items?.length || 0} item(s)</span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        <span className={`font-medium ${(gr.total_amount || 0) < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                          {gr.currency} {(gr.total_amount || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 mr-1 text-gray-400" />
                          {gr.posting_date}
                        </div>
                        <span className="text-xs text-gray-500">{gr.created_by}</span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/p2p/gr-view/${gr.material_document}/${gr.material_document_year}`)}
                          className="text-orange-600 hover:text-orange-700"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default GRList;